import { useMutation, useQuery, NetworkStatus } from '@apollo/client'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'

import Card from '@10xLabs/react-web/components/Card'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import FormLayoutV2 from 'components/v2/FormLayout'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { NewBusinessCustomer } from 'features/BusinessCustomer/components/NewBusinessCustomerForm'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { BusinessCustomerFormUtils } from 'features/BusinessCustomer/utils'
import { useLogin } from 'features/Login/hooks'
import { canWrite } from 'features/Users/utils'
import { GET_BUSINESS_CUSTOMER } from 'features/BusinessCustomer/queries-legacy'
import { CHANGE_BUSINESS_CUSTOMER_BUSINESS_CLIENT } from 'features/BusinessCustomer/mutations'
import {
  ASSIGN_BUSINESS_CUSTOMER_KREDIT_CATEGORY,
  CHANGE_BUSINESS_CLIENT_CUSTOMER_KREDITS_AMOUNT,
  CHANGE_BUSINESS_CUSTOMER_BUSINESS_SITE,
  CHANGE_USER_ACCOUNT_DETAILS,
  CREATE_PLACE_FOR_BUSINESS_CUSTOMER
} from 'features/BusinessCustomer/mutations-legacy'
import { IBusinessCustomerFormValues } from 'features/BusinessCustomer/types'
import { GTBusinessCustomer } from 'gql/types.generated'
import { useNavigate, useParams } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { getNextLink } from 'variables/filters'
import { EUrls } from 'utils/urls'

const messages = defineMessages({
  helmet: {
    id: 'businessCustomer.edit.helmet',
    defaultMessage: 'Edit Business Customer | Nexbus Admin'
  }
})

const EditBusinessCustomerView = (): ReactElement => {
  const intl = useIntl()
  const login = useLogin()
  const canEdit = canWrite(FEATURES.BUSINESS_CUSTOMER, login.accessToken.policies)
  const navigate = useNavigate()
  const { businessCustomerId } = useParams()
  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery<{ businessCustomer: GTBusinessCustomer }>(GET_BUSINESS_CUSTOMER, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !canEdit,
    variables: {
      input: {
        id: businessCustomerId
      }
    }
  })

  const [changeUserAccountDetails, { loading: changeUserAccountDetailsLoading, error: changeUserAccountDetailsError }] =
    useMutation(CHANGE_USER_ACCOUNT_DETAILS, {
      refetchQueries: ['GetBusinessCustomers']
    })
  const [createPlaceForBusinessCustomer, { loading: creatingPlaceLoading, error: creatingPlaceError }] = useMutation(
    CREATE_PLACE_FOR_BUSINESS_CUSTOMER,
    {
      refetchQueries: ['GetBusinessCustomers']
    }
  )
  const [assignBusinessCustomerKreditCategory, { loading: assigningCategoryLoading, error: assigningCategoryError }] =
    useMutation(ASSIGN_BUSINESS_CUSTOMER_KREDIT_CATEGORY, {
      refetchQueries: ['GetBusinessCustomers']
    })
  const [changeBusinessClientCustomerKreditsAmount, { loading: amountLoading, error: amountError }] = useMutation(
    CHANGE_BUSINESS_CLIENT_CUSTOMER_KREDITS_AMOUNT,
    {
      refetchQueries: ['GetBusinessCustomers']
    }
  )
  const [changeBusinessSite, { loading: changeBusinessSiteLoading, error: changeBusinessSiteError }] = useMutation(
    CHANGE_BUSINESS_CUSTOMER_BUSINESS_SITE,
    {
      refetchQueries: ['GetBusinessCustomers']
    }
  )
  const [changeBusinessClient, { loading: changeBusinessClientLoading, error: changeBusinessClientError }] =
    useMutation(CHANGE_BUSINESS_CUSTOMER_BUSINESS_CLIENT, {
      refetchQueries: ['GetBusinessCustomers']
    })

  if (!canEdit) {
    return <ForbiddenView feature={FEATURES.BUSINESS_CUSTOMER} />
  }

  const handleSubmit = async (formValues: IBusinessCustomerFormValues): Promise<void> => {
    const mutationInputValues = BusinessCustomerFormUtils.mapFormToUpdateMutation(
      formValues,
      queryData?.businessCustomer as GTBusinessCustomer
    )
    try {
      if (!isEmpty(mutationInputValues.userAccount)) {
        await changeUserAccountDetails({
          variables: {
            input: mutationInputValues.userAccount
          }
        })
      }
      if (!isEmpty(mutationInputValues.place)) {
        await createPlaceForBusinessCustomer({
          variables: {
            input: mutationInputValues.place
          }
        })
      }
      if (!isEmpty(mutationInputValues.amount)) {
        await changeBusinessClientCustomerKreditsAmount({
          variables: {
            input: mutationInputValues.amount
          }
        })
      }

      if (!isEmpty(mutationInputValues.category)) {
        await assignBusinessCustomerKreditCategory({
          variables: {
            input: mutationInputValues.category
          }
        })
      }

      if (!isEmpty(mutationInputValues.businessSite)) {
        await changeBusinessSite({
          variables: {
            input: mutationInputValues.businessSite
          }
        })
      }
      if (!isEmpty(mutationInputValues.businessClient)) {
        await changeBusinessClient({
          variables: {
            input: mutationInputValues.businessClient
          }
        })
      }
      navigate(getNextLink(EUrls.BUSINESS_CUSTOMERS))
    } catch (error) {
      //do nothing
    }
  }

  let content: ReactElement
  if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (queryError) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="businessCustomer.edit.error.description"
              defaultMessage="There was an error while loading your data"
            />
          </EmptyState.Description>
          <EmptyState.Action>
            <Button
              onClick={(): void => {
                refetch()
              }}
              kind="primary"
            >
              <FormattedMessage id="businessCustomer.edit.error.reload" defaultMessage="Reload" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    const loading =
      changeUserAccountDetailsLoading ||
      creatingPlaceLoading ||
      assigningCategoryLoading ||
      amountLoading ||
      changeBusinessSiteLoading ||
      changeBusinessClientLoading
    const error =
      changeUserAccountDetailsError ||
      creatingPlaceError ||
      assigningCategoryError ||
      amountError ||
      changeBusinessSiteError ||
      changeBusinessClientError
    content = (
      <NewBusinessCustomer
        isShowingKreditSettings={queryData?.businessCustomer.businessClient.isKreditEnabled}
        disableEditSite={queryData?.businessCustomer.hasHomePickupRoute}
        disableEditClient={queryData?.businessCustomer.isEnabled}
        initialValues={BusinessCustomerFormUtils.mapResponseToForm(queryData?.businessCustomer as GTBusinessCustomer)}
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        isEdit
      />
    )
  }

  return <FormLayoutV2.Container>{content}</FormLayoutV2.Container>
}

export default withLastUrl(EditBusinessCustomerView)
